import { useEffect, useMemo, useState, useCallback } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { Collapse, MenuItem, styled, IconButton } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Button } from '../../../../components/buttons'
import { Checkbox } from '../../../../components/inputs/Checkbox'
import { DateInput } from '../../../../components/inputs/DatePicker'
import { Text, Title } from '../../../../components/typography'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import { SprintPreviewTable } from './SprintPreviewTable'
import { NamingRuleFields } from './NamingRuleFields'
import { useNamingRule } from './useNamingRule'
import { useSprintPreview } from './useSprintPreview'
import {
  FormLabel,
  FormRow,
  HelperText,
  InputBox,
  NumberStepInput,
  StyledMuiSelect,
} from './formParts'
import {
  GeneratedSprint,
  LabelParams,
  NamingRule,
  SprintInterval,
  SprintSuffix,
} from './generateSprints'

type Props = {
  open: boolean
  onClose: () => void
  onAdd: (sprints: GeneratedSprint[]) => void
  nameCharactersAllowed?: string
  nameMaxLength?: number
  codeCharactersAllowed?: string
  codeMaxLength?: number
}

const INTERVALS: { value: SprintInterval; labelId: string }[] = [
  { value: '1w', labelId: 'bulkSprint.interval.1w' },
  { value: '2w', labelId: 'bulkSprint.interval.2w' },
  { value: '1month', labelId: 'bulkSprint.interval.1month' },
]

const MAX_COUNT = 52
const MIN_COUNT = 1
// 'YYMMDD' の文字数
const DATE_SUFFIX_LENGTH = 6

// 桁あふれした連番も収まるよう、生成されうる最長のサフィックスで判定する
const maxSuffixLength = (suffix: SprintSuffix, count: number): number =>
  suffix.type === 'date'
    ? DATE_SUFFIX_LENGTH
    : Math.max(
        suffix.digits,
        String(suffix.start + Math.max(count, 1) - 1).length
      )

const validateNamingRule = (
  { prefix, suffix }: NamingRule,
  count: number,
  charactersAllowed?: string,
  maxLength?: number
): string | undefined => {
  if (
    prefix.length > 0 &&
    charactersAllowed &&
    !prefix.match(`^[${charactersAllowed}]*$`)
  ) {
    return (
      intl.formatMessage({ id: 'validator.invalidCharacter.allowed' }) +
      charactersAllowed
    )
  }
  if (maxLength && prefix.length + maxSuffixLength(suffix, count) > maxLength) {
    return intl.formatMessage(
      { id: 'bulkSprint.prefix.tooLong' },
      { max: maxLength }
    )
  }
  return undefined
}

const BulkSprintDialog = ({
  open,
  onClose,
  onAdd,
  nameCharactersAllowed,
  nameMaxLength,
  codeCharactersAllowed,
  codeMaxLength,
}: Props) => {
  const [interval, setInterval] = useState<SprintInterval>('1w')
  const [startDate, setStartDate] = useState<Date | undefined>(new Date())
  const [countInput, setCountInput] = useState<string>('2')
  const [codeEnabled, setCodeEnabled] = useState<boolean>(false)

  const nameRule = useNamingRule('Sprint')
  const codeRule = useNamingRule('')

  const count = useMemo(() => {
    const v = parseInt(countInput, 10)
    if (isNaN(v) || v < MIN_COUNT) return 0
    return Math.min(v, MAX_COUNT)
  }, [countInput])

  const labels = useMemo<LabelParams>(
    () => ({
      name: nameRule.rule,
      code: codeEnabled ? codeRule.rule : undefined,
    }),
    [nameRule.rule, codeRule.rule, codeEnabled]
  )

  const namePrefixError = useMemo(
    () =>
      validateNamingRule(
        nameRule.rule,
        count,
        nameCharactersAllowed,
        nameMaxLength
      ),
    [nameRule.rule, count, nameCharactersAllowed, nameMaxLength]
  )

  // チェックを外したらコードは使われないので、入力が残っていても妨げにしない
  const codePrefixError = useMemo(
    () =>
      codeEnabled
        ? validateNamingRule(
            codeRule.rule,
            count,
            codeCharactersAllowed,
            codeMaxLength
          )
        : undefined,
    [codeEnabled, codeRule.rule, count, codeCharactersAllowed, codeMaxLength]
  )

  const hasError = !!namePrefixError || !!codePrefixError

  const { preview, shift, clear } = useSprintPreview({
    interval,
    startDate,
    count,
    labels,
  })

  useEffect(() => {
    if (open) {
      setInterval('1w')
      setStartDate(new Date())
      setCountInput('2')
      setCodeEnabled(false)
      clear()
      nameRule.reset()
      codeRule.reset()
    }
    // reset/clear は内部状態を初期化するだけで、依存に含めると開くたびに再実行される
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const emptyFieldMessage = useMemo(() => {
    if (preview.length > 0) return undefined
    const missing: string[] = []
    if (!startDate) {
      missing.push(intl.formatMessage({ id: 'bulkSprint.startDate' }))
    }
    if (count <= 0) {
      missing.push(intl.formatMessage({ id: 'bulkSprint.count' }))
    }
    if (missing.length === 0) return undefined
    return intl.formatMessage(
      { id: 'bulkSprint.preview.empty' },
      {
        fields: missing.join(
          intl.formatMessage({ id: 'bulkSprint.preview.empty.separator' })
        ),
      }
    )
  }, [preview.length, startDate, count])

  const handleAdd = useCallback(() => {
    if (preview.length === 0) return
    onAdd(preview)
    onClose()
  }, [preview, onAdd, onClose])

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <StyledDialogTitle>
        <Title>{intl.formatMessage({ id: 'bulkSprint.title' })}</Title>
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <FormSection>
          <FormRow>
            <FormLabel>
              <Text variant="sBold">
                {intl.formatMessage({ id: 'bulkSprint.interval' })}
              </Text>
            </FormLabel>
            <StyledMuiSelect
              value={interval}
              onChange={e => setInterval(e.target.value as SprintInterval)}
              size="small"
            >
              {INTERVALS.map(({ value, labelId }) => (
                <MenuItem key={value} value={value}>
                  {intl.formatMessage({ id: labelId })}
                </MenuItem>
              ))}
            </StyledMuiSelect>
          </FormRow>

          <FormRow>
            <FormLabel>
              <Text variant="sBold">
                {intl.formatMessage({ id: 'bulkSprint.startDate' })}
              </Text>
            </FormLabel>
            <InputBox>
              <DateInput
                value={startDate}
                onChange={v => setStartDate(v)}
                placeholder="YYYY/MM/DD"
              />
            </InputBox>
          </FormRow>

          <FormRow>
            <FormLabel>
              <Text variant="sBold">
                {intl.formatMessage({ id: 'bulkSprint.count' })}
              </Text>
            </FormLabel>
            <NumberStepInput
              value={countInput}
              onChange={setCountInput}
              min={MIN_COUNT}
              max={MAX_COUNT}
            />
            <Text variant="s">
              {intl.formatMessage({ id: 'bulkSprint.countUnit' })}
            </Text>
          </FormRow>

          <NamingRuleFields
            labelId="bulkSprint.nameRule"
            rule={nameRule}
            placeholder="Sprint"
            error={namePrefixError}
          />

          <CodeSection>
            <CodeSectionToggle>
              <Checkbox
                size="s"
                checked={codeEnabled}
                onChange={(_, checked) => setCodeEnabled(checked)}
              />
              <Text variant="sBold">
                {intl.formatMessage({ id: 'bulkSprint.codeSection' })}
              </Text>
              <HelperText>
                <Text variant="2s">
                  {intl.formatMessage({ id: 'bulkSprint.codeSection.help' })}
                </Text>
              </HelperText>
            </CodeSectionToggle>
            <Collapse in={codeEnabled}>
              <CodeFields>
                <NamingRuleFields
                  labelId="bulkSprint.codeRule"
                  rule={codeRule}
                  placeholder={intl.formatMessage({
                    id: 'bulkSprint.prefix.placeholder',
                  })}
                  error={codePrefixError}
                />
              </CodeFields>
            </Collapse>
          </CodeSection>
        </FormSection>

        <SprintPreviewTable
          sprints={preview}
          showCode={codeEnabled}
          emptyMessage={emptyFieldMessage}
          onShift={shift}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <Button colorPattern="monotone" variant="outlined" onClick={onClose}>
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </Button>
        <Button
          colorPattern="skyBlue"
          variant="filled"
          onClick={handleAdd}
          disabled={preview.length === 0 || hasError}
        >
          {intl.formatMessage({ id: 'bulkSprint.add' })}
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    height: '720px',
    maxHeight: '92vh',
    borderRadius: '8px',
    overflow: 'hidden',
  },
})

const StyledDialogContent = styled(DialogContent)({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
})

const StyledDialogTitle = styled(DialogTitle)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  padding: '12px 16px',
})

const FormSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '16px 24px',
  backgroundColor: colorPalette.skyBlue[0],
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  flexShrink: 0,
})

const CodeSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const CodeSectionToggle = styled('label')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  alignSelf: 'flex-start',
  cursor: 'pointer',
})

const CodeFields = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  paddingTop: '10px',
})

const StyledDialogActions = styled(DialogActions)({
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
  padding: '12px 24px',
})

export { BulkSprintDialog }
